import { CanActivateFn } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject } from '@angular/core';
import { catchError, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';

import { LoadingService } from '../services/loading.service';
import { AllLookupsService } from '../services/all-lookups.service';
import { GlobalConfigsService } from '../services/global-configs.service';
import { CommonApiService } from '../../proxy/services/common-api.service';
import { ExecutionStatus, UserInfo } from '../../proxy/models/common';
import { IndexDbKeys, SystemCheckSums } from '../../proxy/models/index-db-keys';
import { IndexDbService } from '../services/indexDb.service';
import { EntityType } from '../../proxy/models/entity-type.enum';
import { UserService } from '../services/user.service';
import { UserInfoService } from '../services/user-info.service';

export const dataGuard: CanActivateFn = (): Observable<boolean> => {
  const loader = inject(LoadingService);
  const commonApi = inject(CommonApiService);
  const indexDb = inject(IndexDbService);
  const user = inject(UserService);
  const userInfo = inject(UserInfoService);
  const allLookups = inject(AllLookupsService);
  const globalConfigs = inject(GlobalConfigsService);
  const oids = inject(OidcSecurityService);
  const destroyRef = inject(DestroyRef);

  function checkAndFetchData(systemCheckSums: SystemCheckSums[]): Observable<boolean> {
    const summary = {
      LookupChecksum: getCheckSumByKey(systemCheckSums, IndexDbKeys.LookupChecksum),
      ConfigChecksum: getCheckSumByKey(systemCheckSums, IndexDbKeys.ConfigChecksum),
    } as any;

    return forkJoin([
      indexDb.getByKey(summary.LookupChecksum.Key),
      indexDb.getByKey(summary.ConfigChecksum.Key),
    ]).pipe(
      takeUntilDestroyed(destroyRef),
      switchMap(([lookups, configs]: [any, any]) => {
        // An earlier app version cached a different shape under these keys, so a
        // cached value can be a string rather than the expected array. Anything
        // that isn't the array shape counts as missing and is refetched, instead
        // of being handed to consumers that expect an array.
        const isLookupsMissing = !lookups || !Array.isArray(lookups.value) || lookups.value.length === 0 ||
          (summary.LookupChecksum.Value && lookups.checkSumValue !== summary.LookupChecksum.Value);

        const isConfigsMissing = !configs || !Array.isArray(configs.value) || configs.value.length === 0 ||
          (summary.ConfigChecksum.Value && configs.checkSumValue !== summary.ConfigChecksum.Value);

        const isUserInfoMissing = !userInfo.userInfo();

        if (!isLookupsMissing && !isConfigsMissing && !isUserInfoMissing) {
          allLookups.setLookups(lookups.value);
          globalConfigs.setConfigs(configs.value);
          loader.hideLoader();
          return of(true);
        }

        const apiCalls: {
          lookups?: Observable<any>;
          configs?: Observable<any>;
          userInfo?: Observable<any>;
        } = {};

        if (isLookupsMissing) {
          apiCalls.lookups = allLookups.getLookupsApi();
        } else {
          allLookups.setLookups(lookups.value);
        }

        if (isConfigsMissing) {
          apiCalls.configs = globalConfigs.getConfigsApi();
        } else {
          globalConfigs.setConfigs(configs.value);
        }

        if (isUserInfoMissing) {
          apiCalls.userInfo = commonApi.executeQuery<UserInfo>(EntityType.systemInfo, {
            userId: user.entityId(),
          });
        }

        return forkJoin(apiCalls).pipe(
          switchMap((results) => {
            const lookupsFailed = isLookupsMissing && results.lookups?.result?.status !== ExecutionStatus.success;
            const configsFailed = isConfigsMissing && results.configs?.result?.status !== ExecutionStatus.success;
            const userInfoFailed = isUserInfoMissing && results.userInfo?.result?.status !== ExecutionStatus.success;

            if (lookupsFailed || configsFailed || userInfoFailed) {
              loader.hideLoader();
              oids.logoff();
              return of(false);
            }

            const saveData: Observable<any>[] = [];

            if (isLookupsMissing && results.lookups?.result?.status === ExecutionStatus.success) {
              saveData.push(indexDb.add({
                key: IndexDbKeys.LookupChecksum,
                checkSumValue: summary[IndexDbKeys.LookupChecksum]?.Value,
                value: results.lookups.records,
              }));
            }

            if (isConfigsMissing && results.configs?.result?.status === ExecutionStatus.success) {
              saveData.push(indexDb.add({
                key: IndexDbKeys.ConfigChecksum,
                checkSumValue: summary[IndexDbKeys.ConfigChecksum]?.Value,
                value: results.configs.abeList,
              }));
            }

            if (isUserInfoMissing && results.userInfo?.result?.status === ExecutionStatus.success) {
              userInfo.saveUserInfo(results.userInfo.records[0]);
            }

            const finalize$ = of(null).pipe(
              map(() => {
                if (isLookupsMissing && results.lookups?.result?.status === ExecutionStatus.success) {
                  allLookups.setLookups(results.lookups.records);
                }
                if (isConfigsMissing && results.configs?.result?.status === ExecutionStatus.success) {
                  globalConfigs.setConfigs(results.configs.abeList);
                }

                loader.hideLoader();
                return true;
              })
            );

            return saveData.length > 0 ? forkJoin(saveData).pipe(switchMap(() => finalize$)) : finalize$;
          }),
          catchError(() => {
            loader.hideLoader();
            oids.logoff();
            return of(false);
          })
        );
      })
    );
  }

  function getCheckSumByKey(systemCheckSums: SystemCheckSums[], key: IndexDbKeys) {
    let keyValue: any = systemCheckSums.find((el) => {
      return el.Key === IndexDbKeys[key];
    });
    return keyValue;
  }

  loader.showLoader();

  return commonApi.getSystemCheckSums().pipe(
    switchMap((res) => {
      if (res.result.status !== ExecutionStatus.success) {
        loader.hideLoader();
        oids.logoff();
        return of(false);
      }

      return checkAndFetchData(res.systemCheckSums);
    }),
    catchError(() => {
      return handleFailureAndLogout();
    }),
    takeUntilDestroyed(destroyRef),
  );

  function handleFailureAndLogout(): Observable<boolean> {
    loader.hideLoader();
    oids.logoff();
    return of(false);
  }
};
